import { useReactiveVar } from '@apollo/client';
import { BottomSheetModalMethods } from '@gorhom/bottom-sheet/lib/typescript/types';
import { RemoteTutorial } from 'components/tutorial/RemoteTutorial';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import { isArray } from 'lodash';
import { useContext, useEffect, useMemo } from 'react';
import {
  featureCountryCode,
  tutorialCompletion,
} from 'states/persistInStorage';
import { Tutorial } from 'types/states';
import { isWeb } from 'utils/deviceUtils';
import { parseFeatureFlagObject } from 'utils/featureFlagUtils';
import {
  completeTutorial,
  getTutorials,
  isBundledTutorial,
  openTutorialModal,
} from 'utils/tutorialUtils';
import { useUiStore } from 'stores/uiStore';

const useTutorialSheet = ({
  bottomSheetModalRef,
  selectedTutorial,
}: {
  bottomSheetModalRef?: React.RefObject<BottomSheetModalMethods>;
  selectedTutorial?: Tutorial;
}) => {
  const tutorialsState = useReactiveVar(tutorialCompletion);
  const featureCountry = useReactiveVar(featureCountryCode);
  const {
    flags: { remote_tutorial },
  } = useContext(FeatureFlagsContext);

  const remoteTutorialData = useMemo(() => {
    const remoteData = parseFeatureFlagObject<RemoteTutorial[]>(
      remote_tutorial,
      'remote_tutorial'
    );
    return isArray(remoteData) ? remoteData : [];
  }, [remote_tutorial]);

  const openedTutorial: Tutorial | undefined = useMemo(() => {
    return getTutorials().find(
      (tutorial) =>
        tutorialsState[tutorial]?.status === 'OPEN' &&
        (isBundledTutorial(tutorial) ||
          remoteTutorialData.find(({ tutorialId }) => tutorialId === tutorial))
    );
  }, [tutorialsState, remoteTutorialData]);

  const canOpenBundledTutorial: Tutorial | undefined = useMemo(() => {
    if (
      selectedTutorial &&
      tutorialsState[selectedTutorial]?.status !== 'COMPLETED'
    ) {
      return selectedTutorial;
    }
  }, [selectedTutorial, tutorialsState]);

  const unCompletedRemoteTutorials: RemoteTutorial[] | undefined = useMemo(
    () =>
      remoteTutorialData.filter(
        ({ tutorialId: remoteTutorialId, countries }) =>
          tutorialsState[remoteTutorialId]?.status !== 'COMPLETED' &&
          (!countries ||
            (featureCountry && countries?.includes(featureCountry)))
      ),
    [remoteTutorialData, tutorialsState, featureCountry]
  );

  const canOpenRemoteTutorial = useMemo(
    () => unCompletedRemoteTutorials?.find(Boolean),
    [unCompletedRemoteTutorials]
  );

  const selectedRemoteData = useMemo(
    () =>
      remoteTutorialData.find(({ tutorialId: id }) => id === openedTutorial),
    [openedTutorial, remoteTutorialData]
  );

  const onDismiss = () => {
    if (openedTutorial) {
      completeTutorial(openedTutorial);
    }
  };

  useEffect(() => {
    if (openedTutorial) {
      bottomSheetModalRef?.current?.present();
      isWeb && openTutorialModal(openedTutorial);
    } else {
      bottomSheetModalRef?.current?.dismiss();
      isWeb && useUiStore.getState().setSheetModal('');
    }
  }, [openedTutorial, bottomSheetModalRef]);

  return {
    openedTutorial,
    canOpenBundledTutorial,
    canOpenRemoteTutorial,
    selectedRemoteData,
    unCompletedRemoteTutorials,
    onDismiss,
  };
};

export default useTutorialSheet;
